import { Event } from '../types';

const MAX_SLUG_TITLE_LENGTH = 72;
const MAX_EXPLICIT_SLUG_LENGTH = 128;

export const slugifyEventText = (value: string): string => {
  const slug = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SLUG_TITLE_LENGTH)
    .replace(/-+$/g, '');

  return slug || 'event';
};

export const stableEventHash = (value: string): string => {
  let hash = 5381;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }

  return (hash >>> 0).toString(36);
};

export const normaliseExplicitEventSlug = (value: string): string => {
  const slug = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_EXPLICIT_SLUG_LENGTH)
    .replace(/-+$/g, '');

  return slug || 'event';
};

type SlugSourceEvent = Pick<Event, 'id' | 'title' | 'start' | 'slug'> & {
  extendedProps?: { slug?: string };
};

export const getEventSlug = (event: SlugSourceEvent): string => {
  // An explicit slug wins outright: organiser events carry an immutable
  // "organiser-<id>" slug, and a scraped event whose fabricated title was
  // repaired may carry its historical hash slug. Checked top-level first, then
  // extendedProps: FullCalendar
  // relocates unknown top-level fields into EventApi.extendedProps, so after
  // a calendar click only the extendedProps copy survives. Other scraped feed
  // events keep using the historical hash form below.
  const topLevelSlug = typeof event.slug === 'string' ? event.slug.trim() : '';
  const extendedSlug = event.extendedProps && typeof event.extendedProps.slug === 'string'
    ? event.extendedProps.slug.trim()
    : '';
  const explicitSlug = topLevelSlug || extendedSlug;
  // Historical generated slugs include a hash after the 72-character readable
  // segment. Keep that complete override up to the route's 128-character
  // limit, otherwise truncation removes the stable identity suffix.
  if (explicitSlug) return normaliseExplicitEventSlug(explicitSlug);

  const readableTitle = slugifyEventText(event.title);
  const identity = [event.id, event.start, event.title].filter(Boolean).join('|');
  return `${readableTitle}-${stableEventHash(identity)}`;
};

export const getEventDetailPath = (event: SlugSourceEvent): string => (
  `/events/${getEventSlug(event)}`
);

export const getAdminEventEditPath = (event: Event): string | null => {
  const submissionId = String(event.id || '').match(/^organiser-(\d+)$/)?.[1];
  if (submissionId) return `/submit-event?edit=${submissionId}&admin=1`;
  if (event.extendedProps?.sourceKey === 'shows' && /^[a-z0-9_]{5,255}$/.test(String(event.id || ''))) {
    return `/submit-event?calendar=${encodeURIComponent(String(event.id))}&admin=1`;
  }
  return null;
};
